import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDown, ArrowUp, Minus, TrendingDown, TrendingUp,
} from 'lucide-react';
import { getMentorStudents } from '../../api/mentorAi';
import { getTeacherCourses } from '../../api/teacher';
import {
  getRiskBand,
  getRiskBandClass,
  RISK_BAND,
} from '../../constants/riskBand';
import Dropdown from '../../components/Dropdown/Dropdown';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import Pagination from '../../components/Pagination/Pagination';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import StudentAvatar from '../../components/Student360/StudentAvatar';
import { riskFactorText } from '../../components/Student360/riskFactor';
import { STUDENT_TIME_ZONE } from '../../components/Student360/time';
import { usePermissions } from '../../hooks/usePermissions';
import { useI18n } from '../../i18n/I18nContext';
import { formatDateTime } from '../../utils/datetime';
import TableScroll from '../Students/TableScroll';
import './StudentStatusDashboard.css';

/**
 * Story 19-1-b: Student Status Dashboard — the "AI risk score" tab of the Students hub.
 *
 * Replaces the Phase C teacher-activity dashboard per Decision #23.
 * Route: /students?tab=at-risk (the old /mentor/students redirects here).
 *
 * Decision #8: TEACHER and MENTOR roles have the same access. The list is the students ASSIGNED to
 * the signed-in teacher or mentor (GET /mentor/students), so for an admin it is empty by design:
 * the empty state says so and points to the Directory, whose filters work across every student.
 */

const PAGE_SIZE = 25;

// GET /mentor/students returns 20 rows unless asked for more and at most 100 per request. The
// sorting and paging below work on the whole filtered list, so every page is read, up to this many.
const FETCH_PAGE_SIZE = 100;
const MAX_FETCH_PAGES = 20;

// The URL keeps the CRM's own chip values; the backend's filter enums spell them differently.
const RISK_BAND_QUERY = { [RISK_BAND.HIGH]: 'high', [RISK_BAND.MEDIUM]: 'med', [RISK_BAND.LOW]: 'low' };
const ACTIVITY_RANGE_QUERY = { 7: '7d', 30: '30d', 90: '90d' };

// A risk band's colour class (`red`...) as the tone the shared badges and chips are drawn in.
const BAND_TONE = {
  red: 'danger', amber: 'warning', green: 'success', grey: 'neutral',
};
const BAND_LABEL_KEY = {
  [RISK_BAND.HIGH]: 'HIGH', [RISK_BAND.MEDIUM]: 'MEDIUM', [RISK_BAND.LOW]: 'LOW',
};

// Reads the filtered list a page at a time. The filters go to the backend: a student row carries
// no course, so the page could not filter by it.
async function fetchAllStudents(filters) {
  const rows = [];
  for (let n = 0; n < MAX_FETCH_PAGES; n += 1) {
    const result = await getMentorStudents({
      ...filters,
      limit: FETCH_PAGE_SIZE,
      offset: n * FETCH_PAGE_SIZE,
    });
    const page = Array.isArray(result) ? result
      : Array.isArray(result?.data) ? result.data
      : [];
    rows.push(...page);
    const total = result?.meta?.total;
    if (page.length < FETCH_PAGE_SIZE || (typeof total === 'number' && rows.length >= total)) break;
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(dateStr, t) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t('statistics.studentStatus.today');
  if (diffDays === 1) return t('statistics.studentStatus.yesterday');
  if (diffDays < 7) return t('statistics.studentStatus.daysAgo', { count: diffDays });
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return t(`statistics.studentStatus.weekAgo${weeks === 1 ? 'One' : 'Other'}`, { count: weeks });
  }
  const months = Math.floor(diffDays / 30);
  return t(`statistics.studentStatus.monthAgo${months === 1 ? 'One' : 'Other'}`, { count: months });
}

// Rising risk is the bad direction (danger), falling the good one.
function getTrendIndicator(trend, t) {
  if (!trend) return null;
  if (trend === 'rising') return { Icon: TrendingUp, label: t('statistics.studentStatus.trendRising') };
  if (trend === 'falling') return { Icon: TrendingDown, label: t('statistics.studentStatus.trendFalling') };
  return { Icon: Minus, label: t('statistics.studentStatus.trendStable') };
}

function sortStudents(list, sort, dir) {
  const sorted = [...list];
  const asc = dir !== 'desc';
  sorted.sort((a, b) => {
    if (sort === 'name') {
      const cmp = (a.name || '').localeCompare(b.name || '');
      return asc ? cmp : -cmp;
    }
    if (sort === 'lastActivity') {
      const aDate = a.lastActivityAt ? new Date(a.lastActivityAt) : new Date(0);
      const bDate = b.lastActivityAt ? new Date(b.lastActivityAt) : new Date(0);
      return asc ? aDate - bDate : bDate - aDate;
    }
    // Default: the risk score (highest first unless the arrow is turned), a missing score last
    const aScore = a.riskScore ?? -1;
    const bScore = b.riskScore ?? -1;
    return asc ? aScore - bScore : bScore - aScore;
  });
  return sorted;
}

// ---------------------------------------------------------------------------
// Risk badge sub-component
// ---------------------------------------------------------------------------

function RiskBadge({ score }) {
  const { t } = useI18n();
  const band = getRiskBand(score);
  const colorClass = getRiskBandClass(band);
  const scoreText = score != null ? t('statistics.studentStatus.riskScoreValue', { score }) : t('statistics.studentStatus.noData');
  return (
    <span title={t('statistics.studentStatus.riskScoreTitle', { score: scoreText })}>
      <StatusBadge
        tone={BAND_TONE[colorClass]}
        className={`ssd-risk-badge ssd-risk-badge--${colorClass}`}
      >
        {scoreText}
      </StatusBadge>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function StudentStatusDashboard({ embedded = false }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { isTeacher } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive filter/sort state from URL query params
  const courseIdParam = searchParams.get('courseId') || '';
  const riskBandParam = searchParams.get('riskBand') || '';
  const lastActivityRangeParam = searchParams.get('lastActivityRange') || '';
  const sortParam = searchParams.get('sort') || 'riskScore';
  const dirParam = searchParams.get('dir') || 'desc';
  const pageParam = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const [allStudents, setAllStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Keep a reference to the current fetch so we can cancel stale responses
  const fetchIdRef = useRef(0);

  // The course, risk and activity filters are applied by the backend, so the list below is the
  // filtered one in full, not the first 20 rows of it.
  const loadStudents = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const list = await fetchAllStudents({
        courseId: courseIdParam || undefined,
        riskBand: RISK_BAND_QUERY[riskBandParam],
        lastActivityRange: ACTIVITY_RANGE_QUERY[lastActivityRangeParam],
      });
      if (fetchId !== fetchIdRef.current) return; // stale response
      setAllStudents(list);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      setError(err || {});
      // Not the rows of the filters that were set before this one.
      setAllStudents([]);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [courseIdParam, riskBandParam, lastActivityRangeParam]);

  // The courses the signed-in teacher or mentor created or is assigned to (an admin gets them
  // all), every page of them: the filter has no "load more".
  const loadCourses = useCallback(async () => {
    try {
      setCourses(await getTeacherCourses());
    } catch {
      // Non-fatal — course filter just won't populate
    }
  }, []);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  // ---------------------------------------------------------------------------
  // Sort, paginate
  // ---------------------------------------------------------------------------

  const sorted = sortStudents(allStudents, sortParam, dirParam);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(pageParam, totalPages);
  const pageStudents = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ---------------------------------------------------------------------------
  // URL param helpers
  // ---------------------------------------------------------------------------

  function setParam(key, value) {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }

  function toggleSort(col) {
    const next = new URLSearchParams(searchParams);
    if (sortParam === col) {
      next.set('dir', dirParam === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sort', col);
      next.set('dir', 'desc');
    }
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    // Only the filters, sort and page go: the rest of the query (`tab=at-risk` in the students
    // hub) says which screen this is.
    const next = new URLSearchParams(searchParams);
    ['courseId', 'riskBand', 'lastActivityRange', 'sort', 'dir', 'page'].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }

  function setPage(p) {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next, { replace: true });
  }

  // ---------------------------------------------------------------------------
  // Sort indicator
  // ---------------------------------------------------------------------------

  function sortArrow(col) {
    if (sortParam !== col) return null;
    return dirParam === 'asc'
      ? <ArrowUp size={13} aria-hidden="true" />
      : <ArrowDown size={13} aria-hidden="true" />;
  }

  function sortHead(col, label) {
    const active = sortParam === col;
    return (
      <th aria-sort={active ? (dirParam === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button
          type="button"
          className={`ssd-th-btn ${active ? 'is-active' : ''}`}
          onClick={() => toggleSort(col)}
        >
          {label}
          {sortArrow(col)}
        </button>
      </th>
    );
  }

  const hasActiveFilters = courseIdParam || riskBandParam || lastActivityRangeParam;
  const forbidden = error?.code === 403;
  const detailPath = (student) => `/mentor/students/${student.id}`;

  // The whole row opens the student; the name link and the buttons inside keep their own meaning.
  const handleRowClick = (event, student) => {
    if (event.target.closest('a, button')) return;
    navigate(detailPath(student));
  };

  // ---------------------------------------------------------------------------
  // Content: loading, error, empty, data
  // ---------------------------------------------------------------------------

  let content;
  if (loading) {
    content = <LoadingState skeleton="table" rows={5} message={t('student360.directory.loading')} />;
  } else if (error) {
    content = (
      <PageEmptyState
        variant={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('student360.detail.noAccessTitle') : t('student360.errors.title')}
        description={forbidden ? t('student360.errors.noAccessDesc') : t('student360.errors.desc')}
        actionLabel={forbidden ? undefined : t('student360.errors.retry')}
        onAction={forbidden ? undefined : loadStudents}
      />
    );
  } else if (pageStudents.length === 0) {
    if (hasActiveFilters) {
      content = (
        <PageEmptyState
          variant="search"
          title={t('statistics.studentStatus.noMatchFilters')}
          actionLabel={t('statistics.studentStatus.clearFilters')}
          onAction={clearFilters}
        />
      );
    } else if (isTeacher()) {
      // A teacher or mentor: the students are the ones a teacher assigns to them
      content = (
        <PageEmptyState
          variant="students"
          title={t('statistics.studentStatus.noStudentsAssigned')}
          description={t('statistics.studentStatus.noStudentsAssignedDesc')}
        />
      );
    } else {
      // An admin has no assigned students by design: say what the tab is and where the whole list is
      content = (
        <PageEmptyState
          variant="students"
          title={t('student360.risk.emptyAdminTitle')}
          description={t('student360.risk.emptyAdminDesc')}
          actionLabel={t('student360.risk.openDirectory')}
          onAction={() => navigate('/students?atRisk=true')}
        />
      );
    }
  } else {
    content = (
      <div className="ui-table-card ssd-table-wrap">
        <div className="ssd-head">
          <span className="ssd-count">{t('student360.directory.total', { count: sorted.length })}</span>
          <span className="ssd-explain">{t('student360.risk.explain')}</span>
        </div>
        <TableScroll>
          <table className="ui-table ssd-table">
            <thead>
              <tr>
                {sortHead('name', t('student360.directory.cols.student'))}
                {sortHead('riskScore', t('statistics.studentStatus.colRiskScore'))}
                <th>{t('statistics.studentStatus.colTrend')}</th>
                <th>{t('statistics.studentStatus.colTopConcern')}</th>
                {sortHead('lastActivity', t('statistics.studentStatus.colLastActivity'))}
                <th>{t('statistics.studentStatus.colLastAssessed')}</th>
              </tr>
            </thead>
            <tbody>
              {pageStudents.map((student) => {
                const trend = getTrendIndicator(student.trend, t);
                const relativeActivity = formatRelativeTime(student.lastActivityAt, t);
                const relativeAssessed = formatRelativeTime(student.riskScoreAt, t);
                const concern = student.topRiskFactor ? riskFactorText(t, student.topRiskFactor) : null;
                return (
                  <tr
                    key={student.id}
                    className="ssd-student-row is-clickable"
                    onClick={(event) => handleRowClick(event, student)}
                  >
                    <td className="ssd-name-cell">
                      <Link
                        className="ui-cell-person ssd-student"
                        to={detailPath(student)}
                        title={t('statistics.studentStatus.viewDetailsFor', { name: student.name })}
                      >
                        <StudentAvatar name={student.name} seed={student.email || student.id} />
                        <span className="ui-cell-person__text">
                          <span className="ui-cell-person__name ssd-student-name">{student.name}</span>
                          {student.email && (
                            <span className="ui-cell-person__sub ssd-student-email">{student.email}</span>
                          )}
                        </span>
                      </Link>
                    </td>
                    <td className="ssd-risk-cell" data-label={t('statistics.studentStatus.colRiskScore')}>
                      <RiskBadge score={student.riskScore} />
                    </td>
                    <td className="ssd-trend-cell" data-label={t('statistics.studentStatus.colTrend')}>
                      {trend ? (
                        <span className={`ssd-trend ssd-trend--${student.trend}`}>
                          <trend.Icon size={16} aria-hidden="true" />
                          {trend.label}
                        </span>
                      ) : (
                        <span className="ssd-na">—</span>
                      )}
                    </td>
                    <td className="ssd-concern-cell" data-label={t('statistics.studentStatus.colTopConcern')}>
                      {concern && concern.label ? (
                        <span className="ssd-concern" title={concern.hint || undefined}>{concern.label}</span>
                      ) : (
                        <span className="ssd-na">—</span>
                      )}
                    </td>
                    <td className="ssd-activity-cell" data-label={t('statistics.studentStatus.colLastActivity')}>
                      {relativeActivity ? (
                        <span title={formatDateTime(student.lastActivityAt, { lang, timeZone: STUDENT_TIME_ZONE })}>
                          {relativeActivity}
                        </span>
                      ) : (
                        <span className="ssd-na">—</span>
                      )}
                    </td>
                    <td className="ssd-assessed-cell" data-label={t('statistics.studentStatus.colLastAssessed')}>
                      {relativeAssessed ? (
                        <span title={formatDateTime(student.riskScoreAt, { lang, timeZone: STUDENT_TIME_ZONE })}>
                          {relativeAssessed}
                        </span>
                      ) : (
                        <span className="ssd-na">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
        {totalPages > 1 && (
          <div className="ui-table-footer">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              total={sorted.length}
              limit={PAGE_SIZE}
              onPrev={() => setPage(currentPage - 1)}
              onNext={() => setPage(currentPage + 1)}
            />
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="ssd-page">
      {!embedded && (
        <div className="page-head">
          <div>
            <h1 className="page-title">{t('statistics.studentStatus.title')}</h1>
            <p className="page-sub">{t('statistics.studentStatus.subtitle')}</p>
          </div>
        </div>
      )}

      {/* Filters: course, risk band, last activity — the same toolbar as the Directory */}
      <div className="ssd-toolbar">
        <Dropdown
          className="ssd-dd ssd-dd--course"
          value={courseIdParam}
          onChange={(val) => setParam('courseId', val)}
          options={[
            { value: '', label: t('statistics.studentStatus.allCourses') },
            ...courses.map((c) => ({ value: c.id, label: c.title || c.name })),
          ]}
          placeholder={t('statistics.studentStatus.allCourses')}
          ariaLabel={t('statistics.studentStatus.courseFilterAriaLabel')}
          size="sm"
        />

        <div className="ui-chip-row ui-chip-row--wrap ssd-chip-row" role="group" aria-label={t('statistics.studentStatus.riskLabel')}>
          {[RISK_BAND.HIGH, RISK_BAND.MEDIUM, RISK_BAND.LOW].map((band) => (
            <button
              key={band}
              type="button"
              className={`ui-chip ssd-chip-risk ${riskBandParam === band ? 'is-active' : ''}`}
              data-tone={BAND_TONE[getRiskBandClass(band)]}
              onClick={() => setParam('riskBand', riskBandParam === band ? '' : band)}
              aria-pressed={riskBandParam === band}
            >
              {t(`student360.riskBand.${BAND_LABEL_KEY[band]}`)}
            </button>
          ))}
        </div>

        <Dropdown
          className="ssd-dd ssd-dd--activity"
          value={lastActivityRangeParam}
          onChange={(val) => setParam('lastActivityRange', val)}
          options={[
            { value: '', label: t('student360.risk.activityAny') },
            { value: '7', label: t('student360.risk.activity7') },
            { value: '30', label: t('student360.risk.activity30') },
            { value: '90', label: t('student360.risk.activity90') },
          ]}
          ariaLabel={t('statistics.studentStatus.lastActivityLabel')}
          size="sm"
        />

        {hasActiveFilters && (
          <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm ssd-clear-btn" onClick={clearFilters}>
            {t('statistics.studentStatus.clearFilters')}
          </button>
        )}
      </div>

      {content}
    </div>
  );
}

export default StudentStatusDashboard;
